"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  X, Check, ChevronDown, GripVertical, Plus, Info,
  MessageSquare, FileText, SlidersHorizontal, LayoutList, Search, Database, ArrowDownLeft, Pin, PinOff, MoreHorizontal,
  ArrowUp, ArrowDown, ArrowRight, ArrowLeft, Eye, EyeOff, Settings, SquarePlus,
} from "lucide-react";
import { toast } from "@/lib/toast";
import type { BoardProperties, BoardProperty } from "@/types/board";
import type { Block, PagePanel, PageSettings } from "@/types/block";
import { PROPERTY_TYPES, getPropertyLabel } from "@/components/tailwind/board/propertyTypes";
import { usePropertyOrderKey, useBoard } from "@/contexts/boardContext";
import { useComputedNotes } from "@/hooks/use-computedNotes";
import { AnchoredPopover, SelfAnchoredPopover } from "@/components/tailwind/ui/anchored-popover";
import {
  LayoutEditorPropertiesPreviewRows,
  LayoutEditorPropertyValuePreview,
} from "@/components/tailwind/ui/LayoutEditorPropertyPreview";
import { generatePropertyId } from "@/lib/propertyIdGenerator";
import { renderIcon } from "@/components/tailwind/editor/renderIcon";
import { createPropertyMutation } from "@/indexedDB";
import { isEmptyPropertyValue } from "@/lib/propertyValueEmpty";
import { useSlidingReorder } from "@/hooks/use-sliding-reorder";
import { HorizontalScrollStrip } from "@/components/tailwind/ui/HorizontalScrollStrip";
import { DropdownMenu, DropdownMenuItem } from "@/components/tailwind/ui/dropdown-menu";

// ─── Types ─────────────────────────────────────────────────────────────────────
type Structure = "simple" | "tabbed";
interface PageLayoutEditorState {
  structure: Structure;
  inlineComments: "default" | "minimal" | "hidden";
  pageDiscussions: boolean;
  propertyIcons: boolean;
  fullWidth: boolean;
  dbTemplates: boolean;
  /** Show names next to icons in the heading's pinned row. */
  pinnedPropertyLabels: boolean;
}
interface Module { id: string; name: string; locked?: boolean; icon: React.ReactNode; }

type ModuleId = "heading" | "properties" | "discussions" | "content";
interface CustomLayoutSavePayload {
  pageSettings: PageSettings;
}

type PanelCard = {
  id: string;
  name: string;
  type: string;
  icon: React.ReactNode;
  propertyId?: string;
  /**
   * Set when this card was added from a "Create new property" template.
   * Holds the schema type slug (e.g. "text", "date") so that on Apply we
   * can create the property in the datasource and assign a real propertyId.
   */
  propertyType?: string;
};

const layoutEditorRootClass =
  "flex flex-col bg-[#fbfbfc] dark:bg-[#191919]";

const layoutEditorShellClass =
  "rounded-t-xl rounded-b-none border-[0.5px] border-[rgba(15,15,15,0.09)] bg-white shadow-[0_0_0_1.5px_rgba(35,131,226,0.28),0_9px_12px_rgba(15,15,15,0.1)] dark:border-[rgba(255,255,255,0.09)] dark:bg-[#202020] dark:shadow-[0_0_0_1.5px_rgba(35,131,226,0.32),0_9px_12px_rgba(15,15,15,0.1)]";

const layoutEditorModuleFrameClass =
  "rounded-lg border-[0.5px] border-[rgba(15,15,15,0.09)] bg-white shadow-[inset_0_0_0_0.5px_rgba(15,15,15,0.09)] dark:border-[rgba(255,255,255,0.09)] dark:bg-[#202020] dark:shadow-[inset_0_0_0_0.5px_rgba(255,255,255,0.09)]";

const layoutEditorModuleHeaderClass =
  "relative h-8 flex shrink-0 items-center gap-1 px-1.5 bg-[#dfeefb] text-[14px] font-medium text-[#206694] dark:bg-[#2a3347] dark:text-[#9ecbff]";

const layoutEditorAccentIconClass = "text-[#206694] dark:text-[#9ecbff]";

const layoutEditorRingDragOverClass =
  "ring-2 ring-[rgba(35,131,226,0.28)] ring-offset-0 dark:ring-[rgba(35,131,226,0.32)]";

// ─── Toggle ────────────────────────────────────────────────────────────────────
function Toggle({ id, checked, onChange }: { id: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button id={id} type="button" role="switch" aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-[18px] w-[32px] shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 ${checked ? "bg-[#2783de]" : "bg-gray-300 dark:bg-zinc-600"}`}>
      <span className={`inline-block h-[14px] w-[14px] rounded-full bg-white shadow transition-transform duration-200 ${checked ? "translate-x-[15px]" : "translate-x-[2px]"}`} />
    </button>
  );
}

// ─── Module Card ───────────────────────────────────────────────────────────────
function ModuleCard({ mod, dragging, dragOver, active, configureRef, onDragStart, onDragOver, onDragLeave, onDrop, onDragEnd, onRemove, onConfigure, onGripPointerDown, propertiesPreviewContent, pinnedPreviewContent, pageTitle, pageIcon }: {
  mod: Module; dragging: boolean; dragOver: boolean;
  active?: boolean;
  /** Anchor for the settings popover (the header's "…" button). */
  configureRef?: React.Ref<HTMLButtonElement>;
  onDragStart: () => void; onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: () => void; onDragEnd: () => void; onRemove: () => void;
  /** Present → header shows a "…" button that opens the module's settings. */
  onConfigure?: () => void;
  onGripPointerDown?: (e: React.PointerEvent) => void;
  propertiesPreviewContent: React.ReactNode;
  /** Heading: the pinned-properties row rendered under the title. */
  pinnedPreviewContent?: React.ReactNode;
  pageTitle?: string;
  pageIcon?: string;
}) {
  const previewContent: Record<string, React.ReactNode> = {
    heading: (
      <div className="px-3 pb-3">
        {pageIcon ? (
          <div className="mb-2 px-2">
            <div className="flex h-[78px] w-[78px] items-center justify-center text-[78px] leading-none">
              <span role="img" aria-label="Page icon">{renderIcon(pageIcon, { style: { width: 78, height: 78 } })}</span>
            </div>
          </div>
        ) : null}
        <button
          type="button"
          className="flex items-center gap-1 text-[14px] text-muted-foreground/60 mb-2 hover-component rounded px-1 -mx-1 py-0.5"
        >
          <ArrowDownLeft className="h-3.5 w-3.5 shrink-0 opacity-50" />
          No backlinks
        </button>
        <div className="px-2">
          <div
            className={`text-[40px] font-bold leading-[1.2] tracking-tight ${pageTitle ? "text-foreground" : "text-muted-foreground/30"}`}
            style={{ fontFamily: "ui-sans-serif, system-ui, sans-serif" }}
          >
            {pageTitle || "New page"}
          </div>
        </div>
        {pinnedPreviewContent}
      </div>
    ),
    properties: <div className="overflow-hidden px-0 pb-1 text-[14px]">{propertiesPreviewContent}</div>,
    discussions: (
      <div className="px-3 pb-3">
        <p className="text-[11px] font-medium text-muted-foreground mb-2">Comments</p>
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 rounded-full bg-gradient-to-br from-violet-400 to-blue-500 shrink-0" />
          <div className="flex-1 rounded-md border border-border/50 px-2.5 py-1.5 text-[13px] text-muted-foreground/50">Add a comment…</div>
        </div>
      </div>
    ),
    content: (
      <div className="px-3 pb-3 min-h-[48px]">
        <span className="text-[13px] text-muted-foreground/40">Empty</span>
      </div>
    ),
  };

  return (
    <div
      draggable={!mod.locked}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      className={`relative w-full select-none transition-all duration-150 ${layoutEditorModuleFrameClass}
        ${mod.id === "discussions" ? "max-h-[300px] overflow-y-auto clean-scrollbar" : mod.id === "content" ? "max-h-[min(480px,55vh)] overflow-y-auto clean-scrollbar" : "overflow-hidden"}
        ${dragging ? "opacity-40 scale-[0.98]" : "opacity-100"}
        ${dragOver || active ? layoutEditorRingDragOverClass : "hover:border-[rgba(35,131,226,0.4)] dark:hover:border-[rgba(35,131,226,0.5)] hover:shadow-[0_0_0_1px_rgba(35,131,226,0.15),inset_0_0_0_1px_rgba(35,131,226,0.15)] dark:hover:shadow-[0_0_0_1px_rgba(35,131,226,0.25),inset_0_0_0_1px_rgba(35,131,226,0.25)]"}`}
    >
      <div className="relative rounded-lg overflow-hidden">
        <div
          onPointerDown={mod.locked ? undefined : onGripPointerDown}
          className={`${layoutEditorModuleHeaderClass} ${mod.locked ? "" : "cursor-grab active:cursor-grabbing"}`}
        >
          {!mod.locked && <GripVertical className={`h-4 w-4 shrink-0 ${layoutEditorAccentIconClass}`} />}
          <span className="flex-1 truncate pe-6">{mod.name}</span>
          {onConfigure && (
            <button
              ref={configureRef}
              type="button"
              aria-label={`${mod.name} settings`}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onConfigure(); }}
              className={`absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded hover:bg-black/[0.04] dark:hover:bg-white/[0.06] ${layoutEditorAccentIconClass} cursor-pointer`}
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          )}
          {!mod.locked && !onConfigure && (
            <button
              type="button"
              aria-label="Remove"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onRemove(); }}
              className={`absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded hover:bg-black/[0.04] dark:hover:bg-white/[0.06] ${layoutEditorAccentIconClass} cursor-pointer`}
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
        <div className="pt-3">{previewContent[mod.id] ?? <div className="px-3 pb-3 text-[13px] text-muted-foreground/40">Preview</div>}</div>
      </div>
      {(mod.id === "discussions" || mod.id === "content") && (
        <div className="pointer-events-none absolute left-0 right-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-white dark:to-[#202020]" />
      )}
    </div>
  );
}

function PanelPropertyCard({
  card,
  valueContent,
  active,
  menuRef,
  onMenu,
  onRemove,
  onGripPointerDown,
}: {
  card: PanelCard;
  valueContent: React.ReactNode;
  active?: boolean;
  menuRef?: (el: HTMLButtonElement | null) => void;
  onMenu?: () => void;
  onRemove?: () => void;
  onGripPointerDown?: (e: React.PointerEvent) => void;
}) {
  return (
    <div
      role="presentation"
      className={`layout-content relative w-full overflow-hidden rounded-lg transition-all ${layoutEditorModuleFrameClass} ${active ? layoutEditorRingDragOverClass : "hover:border-[rgba(35,131,226,0.4)] dark:hover:border-[rgba(35,131,226,0.5)] hover:shadow-[0_0_0_1px_rgba(35,131,226,0.15),inset_0_0_0_1px_rgba(35,131,226,0.15)] dark:hover:shadow-[0_0_0_1px_rgba(35,131,226,0.25),inset_0_0_0_1px_rgba(35,131,226,0.25)]"}`}
    >
      <div onPointerDown={onGripPointerDown} className={`${layoutEditorModuleHeaderClass} ${onGripPointerDown ? "cursor-grab active:cursor-grabbing" : ""}`}>
        <GripVertical className={`h-4 w-4 shrink-0 ${layoutEditorAccentIconClass}`} />
        <div className="truncate pe-6">{card.name}</div>
      </div>

      <div className="p-3">
        <div className="relative w-full">
          <button
            type="button"
            className="block w-full min-h-[34px] rounded-[4px] px-[6px] py-[7px] text-[14px] leading-5 text-left text-foreground cursor-default"
          >
            {valueContent}
          </button>
        </div>
      </div>

      <div className="absolute top-1 right-1 z-10">
        {onMenu ? (
          <button
            ref={menuRef}
            type="button"
            className={`flex h-6 w-6 items-center justify-center rounded transition-colors hover:bg-black/[0.04] dark:hover:bg-white/[0.06] ${layoutEditorAccentIconClass}`}
            aria-label={`${card.name} options`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onMenu(); }}
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        ) : (
          <button
            type="button"
            className={`flex h-6 w-6 items-center justify-center rounded transition-colors hover:bg-black/[0.04] dark:hover:bg-white/[0.06] ${layoutEditorAccentIconClass}`}
            aria-label="Remove panel property"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={onRemove}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function GroupCard({
  title,
  dragging,
  dragOver,
  active,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  menuRef,
  onMenu,
  onGripPointerDown,
  content,
}: {
  title: string;
  dragging: boolean;
  dragOver: boolean;
  active?: boolean;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: () => void;
  onDragEnd: () => void;
  menuRef?: (el: HTMLButtonElement | null) => void;
  onMenu: () => void;
  onGripPointerDown?: (e: React.PointerEvent) => void;
  content: React.ReactNode;
}) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      className={`relative w-full select-none overflow-hidden rounded-lg transition-all duration-150 ${layoutEditorModuleFrameClass} ${dragging ? "opacity-40 scale-[0.98]" : "opacity-100"} ${dragOver || active ? layoutEditorRingDragOverClass : "hover:border-[rgba(35,131,226,0.4)] dark:hover:border-[rgba(35,131,226,0.5)] hover:shadow-[0_0_0_1px_rgba(35,131,226,0.15),inset_0_0_0_1px_rgba(35,131,226,0.15)] dark:hover:shadow-[0_0_0_1px_rgba(35,131,226,0.25),inset_0_0_0_1px_rgba(35,131,226,0.25)]"}`}
    >
      <div onPointerDown={onGripPointerDown} className={`${layoutEditorModuleHeaderClass} cursor-grab active:cursor-grabbing`}>
        <GripVertical className={`h-4 w-4 shrink-0 ${layoutEditorAccentIconClass}`} />
        <span className="flex-1 truncate pe-6">{title}</span>
        <button
          ref={menuRef}
          type="button"
          aria-label={`${title} options`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onMenu(); }}
          className={`absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded hover:bg-black/[0.04] dark:hover:bg-white/[0.06] ${layoutEditorAccentIconClass} cursor-pointer`}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </div>
      <div className="overflow-hidden px-0 pb-1 pt-3 text-[14px]">{content}</div>
    </div>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────────────
const ALL_MODULES: Module[] = [
  { id: "heading", name: "Heading", locked: true, icon: <FileText className="h-3.5 w-3.5" /> },
  { id: "properties", name: "Property group", icon: <SlidersHorizontal className="h-3.5 w-3.5" /> },
  { id: "discussions", name: "Page discussions", icon: <MessageSquare className="h-3.5 w-3.5" /> },
  { id: "content", name: "Content", icon: <LayoutList className="h-3.5 w-3.5" /> },
];

export default function CustomLayoutEditor({
  pageId,
  pageName,
  databaseTitle,
  onClose,
  initialSettings,
  boardId,
  dataSourceId,
  workspaceId,
  boardProperties,
  pagePropertyValues,
  previewPages,
  propertyOrder: propertyOrderProp,
  onSaveLayout,
}: {
  pageId: string;
  pageName: string;
  /** Data source / database label shown in the top bar */
  databaseTitle?: string;
  /** Collection-view block id (used for formula/rollup preview). */
  boardId: string;
  /** Datasource the editor is operating on — needed to create new properties. */
  dataSourceId?: string;
  /** Workspace the datasource belongs to — needed for createPropertyMutation. */
  workspaceId?: string;
  onClose: () => void;
  initialSettings?: Partial<PageSettings>;
  boardProperties?: BoardProperties;
  pagePropertyValues?: Record<string, any>;
  /** Pages in the active datasource for preview switching */
  previewPages?: Block[];
  /** Same order as the properties sidebar when available */
  propertyOrder?: string[];
  onSaveLayout?: (scope: "all_pages" | "this_page", payload: CustomLayoutSavePayload) => Promise<void> | void;
}) {
  const [settings, setSettings] = useState<PageLayoutEditorState>({
    structure: (initialSettings?.structure || "simple") as Structure,
    inlineComments: (initialSettings?.inlineComments || "default") as "default" | "minimal" | "hidden",
    pageDiscussions: initialSettings?.pageDiscussions ?? true,
    propertyIcons: initialSettings?.propertyIcons ?? true,
    fullWidth: initialSettings?.fullWidth ?? false,
    dbTemplates: true,
    pinnedPropertyLabels: initialSettings?.pinnedPropertyLabels ?? true,
  });
  const set = <K extends keyof PageLayoutEditorState>(k: K, v: PageLayoutEditorState[K]) =>
    setSettings(p => ({ ...p, [k]: v }));

  const visiblePanels = (initialSettings?.visiblePanels ?? []) as PagePanel[];
  const defaultModuleOrder: ModuleId[] = ["heading", "properties", "discussions", "content"];
  const initialModuleOrder: ModuleId[] = visiblePanels.length > 0
    ? visiblePanels
      .map((panel) => {
        if (panel.type === "heading") return "heading" as ModuleId;
        if (panel.type === "property_group" || panel.type === "property_single") return "properties" as ModuleId;
        if (panel.type === "page_discussions") return "discussions" as ModuleId;
        if (panel.type === "content") return "content" as ModuleId;
        return null;
      })
      .filter((moduleId): moduleId is ModuleId => !!moduleId)
    : defaultModuleOrder;
  const normalizedInitialModuleOrder: ModuleId[] = (() => {
    const orderedUnique = initialModuleOrder.filter(
      (moduleId, idx, arr) => arr.indexOf(moduleId) === idx
    );
    const withHeading = orderedUnique.includes("heading")
      ? orderedUnique
      : (["heading", ...orderedUnique] as ModuleId[]);
    const withContent = withHeading.includes("content")
      ? withHeading
      : ([...withHeading, "content"] as ModuleId[]);
    return withContent;
  })();
  const [modules, setModules] = useState<Module[]>(
    normalizedInitialModuleOrder
      .map((moduleId) => ALL_MODULES.find((m) => m.id === moduleId))
      .filter(Boolean) as Module[]
  );
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const [inlineOpen, setInlineOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [showResetLayoutModal, setShowResetLayoutModal] = useState(false);
  const [addToPanelMode, setAddToPanelMode] = useState(false);
  const [addToPreviewMode, setAddToPreviewMode] = useState(false);
  const [panelSearch, setPanelSearch] = useState("");
  const [panelCards, setPanelCards] = useState<PanelCard[]>([]);
  const [panelStagingCards, setPanelStagingCards] = useState<PanelCard[]>([]);
  const [topPreviewOrder, setTopPreviewOrder] = useState<string[]>(["properties"]);
  // saved as the heading panel's propertyIds
  const [pinnedIds, setPinnedIds] = useState<string[]>([]);
  // null → no relations group panel; the "relations" token in topPreviewOrder places it
  const [relationsGroupIds, setRelationsGroupIds] = useState<string[] | null>(null);
  const [openItemMenu, setOpenItemMenu] = useState<string | null>(null);
  const [groupMenuSearch, setGroupMenuSearch] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const advancedRowRef = useRef<HTMLDivElement | null>(null);
  const itemMenuBtnRefs = useRef(new Map<string, HTMLButtonElement>());
  const setItemMenuRef = (key: string) => (el: HTMLButtonElement | null) => {
    if (el) itemMenuBtnRefs.current.set(key, el);
    else itemMenuBtnRefs.current.delete(key);
  };
  const [headingConfigOpen, setHeadingConfigOpen] = useState(false);
  const [headingSearch, setHeadingSearch] = useState("");
  const headingMenuBtnRef = useRef<HTMLButtonElement | null>(null);
  const [topPreviewDragIdx, setTopPreviewDragIdx] = useState<number | null>(null);
  const [topPreviewDragOver, setTopPreviewDragOver] = useState<number | null>(null);
  const [draggingStagingCardId, setDraggingStagingCardId] = useState<string | null>(null);
  const [isPreviewDropActive, setIsPreviewDropActive] = useState(false);
  const addToPanelTriggerRef = useRef<HTMLButtonElement | null>(null);
  const settingsPanelRef = useRef<HTMLElement | null>(null);

  const { getNotesByDataSourceId, getDataSource, setPropertyOrder, propertyOrder: contextPropertyOrder, updateDataSource, refreshNotes, getRelationNoteTitle, getValidRelationIds } = useBoard();
  const propertyOrderKey = usePropertyOrderKey(boardId);

  // Preview shows related page titles; unresolved ids render nothing.
  const resolveRelationTitle = (noteId: string, linkedDatabaseId: string): string => {
    const [valid] = getValidRelationIds([noteId], linkedDatabaseId);
    if (!valid) return "";
    return getRelationNoteTitle(valid, linkedDatabaseId, "");
  };
  const [previewPageId, setPreviewPageId] = useState(pageId);
  const [pagePickerOpen, setPagePickerOpen] = useState(false);
  const [pageSearchQuery, setPageSearchQuery] = useState("");
  const pagePickerTriggerRef = useRef<HTMLButtonElement>(null);
  const closePagePicker = () => {
    setPagePickerOpen(false);
    setPageSearchQuery("");
  };

  useEffect(() => {
    setPreviewPageId(pageId);
  }, [pageId]);

  const fallbackPreviewBlock = useMemo(
    () =>
      ({
        _id: pageId,
        blockType: "page",
        workspaceId: "",
        workareaId: null,
        parentId: "",
        parentType: "block",
        status: "alive",
        value: {
          title: pageName,
          databaseProperties: pagePropertyValues ?? {},
          formulaErrors: {},
        },
      }) as Block,
    [pageId, pageName, pagePropertyValues],
  );

  const selectedRawPage = useMemo(() => {
    const pages = (previewPages ?? []).filter(Boolean);
    if (pages.length === 0) return fallbackPreviewBlock;
    return pages.find((p) => p._id === previewPageId) ?? pages[0] ?? fallbackPreviewBlock;
  }, [previewPages, previewPageId, fallbackPreviewBlock]);

  const computedPreviewNotes = useComputedNotes(
    selectedRawPage ? [selectedRawPage] : [],
    boardProperties || {},
    boardId,
    getNotesByDataSourceId,
    getDataSource,
    refreshNotes,
  );
  const previewNote = computedPreviewNotes[0] ?? selectedRawPage;
  const previewDb =
    ((previewNote?.value as { databaseProperties?: Record<string, unknown> })?.databaseProperties) ?? {};
  const previewFormulaErrors = (previewNote?.value as { formulaErrors?: Record<string, string> })?.formulaErrors;
  const previewTitleRaw = String((previewNote?.value as { title?: string })?.title ?? "").trim();
  const hasPreviewTitle = previewTitleRaw.length > 0;
  const previewTitle = hasPreviewTitle ? previewTitleRaw : "New page";
  const previewIcon = String((previewNote?.value as { icon?: string })?.icon ?? "").trim();

  // group reorder edits live here until save writes them to the board's order
  const [localPropertyOrder, setLocalPropertyOrder] = useState<string[] | null>(null);
  const resolvedPropertyOrder = useMemo(() => {
    if (localPropertyOrder) return localPropertyOrder;
    if (propertyOrderProp && propertyOrderProp.length > 0) return propertyOrderProp;
    return Object.keys(boardProperties || {});
  }, [localPropertyOrder, propertyOrderProp, boardProperties]);

  const pickerPages = useMemo(() => {
    const pages = previewPages ?? [];
    if (pages.length > 0) return pages;
    return [fallbackPreviewBlock];
  }, [previewPages, fallbackPreviewBlock]);

  const filteredPickerPages = useMemo(() => {
    const q = pageSearchQuery.trim().toLowerCase();
    if (!q) return pickerPages;
    return pickerPages.filter((p) => {
      const t = String((p.value as { title?: string })?.title ?? "").toLowerCase();
      return t.includes(q);
    });
  }, [pickerPages, pageSearchQuery]);

  const handleDragStart = (i: number) => { setDragIdx(i); };
  const handleDragOver = (i: number, e: React.DragEvent) => {
    e.preventDefault();
    if (dragIdx !== null) setDragOver(i);
  };
  const handleDrop = (i: number) => {
    if (dragIdx === null || dragIdx === i) {
      setDragIdx(null);
      setDragOver(null);
      return;
    }
    const next = [...modules];
    const [m] = next.splice(dragIdx, 1);
    if (!m) {
      setDragIdx(null);
      setDragOver(null);
      return;
    }
    next.splice(i, 0, m);
    setModules(next);
    setDragIdx(null);
    setDragOver(null);
  };

  const inlineLabels: Record<string, string> = { default: "Default", minimal: "Minimal", hidden: "Hidden" };

  const propertyEntries = Object.entries(boardProperties || {}) as Array<[string, { name?: string; type?: string }]>;

  const getPropertyTypeMeta = (type: string) => {
    const normalizedType = type === "files_media" ? "file" : type;
    const match = PROPERTY_TYPES.find((p) => p.type === normalizedType) ?? PROPERTY_TYPES.find((p) => p.type === "text");
    return {
      type: match?.type || "text",
      label: match?.label || "Text",
      Icon: match?.icon,
    };
  };

  const renderTypeIcon = (type: string) => {
    const meta = getPropertyTypeMeta(type);
    const Icon = meta.Icon;
    return Icon ? <Icon className="h-4 w-4" /> : null;
  };

  // every property placed outside the group: cards, staging, pinned
  const panelPropertyIds = useMemo(
    () =>
      new Set([
        ...[...panelCards, ...panelStagingCards]
          .map((card) => card.propertyId)
          .filter((propertyId): propertyId is string => !!propertyId),
        ...pinnedIds,
        ...(relationsGroupIds ?? []),
      ]),
    [panelCards, panelStagingCards, pinnedIds, relationsGroupIds],
  );

  const moveTopItem = (id: string, dir: -1 | 1) => {
    setTopPreviewOrder((prev) => {
      const i = prev.indexOf(id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      const a = next[i];
      const b = next[j];
      if (a === undefined || b === undefined) return prev;
      next[i] = b;
      next[j] = a;
      return next;
    });
  };
  const removeCardFromPreview = (card: PanelCard) => {
    setPanelCards((prev) => prev.filter((c) => c.id !== card.id));
    setTopPreviewOrder((prev) => prev.filter((id) => id !== card.id));
  };
  const moveCardToPanel = (card: PanelCard) => {
    removeCardFromPreview(card);
    setPanelStagingCards((prev) => (prev.some((c) => c.id === card.id) ? prev : [...prev, card]));
  };
  // a card's property joins (or creates, in the card's slot) the relations group
  const convertCardToRelationsGroup = (card: PanelCard) => {
    const propertyId = card.propertyId;
    if (!propertyId) return;
    setPanelCards((prev) => prev.filter((c) => c.id !== card.id));
    setTopPreviewOrder((prev) => {
      if (prev.includes("relations")) return prev.filter((id) => id !== card.id);
      return prev.map((id) => (id === card.id ? "relations" : id));
    });
    setRelationsGroupIds((prev) => (prev?.includes(propertyId) ? prev : [...(prev ?? []), propertyId]));
  };
  const dissolveRelationsGroup = () => {
    setRelationsGroupIds(null);
    setTopPreviewOrder((prev) => prev.filter((id) => id !== "relations"));
  };
  const moveRelationsGroupToPanel = () => {
    const ids = relationsGroupIds ?? [];
    setPanelStagingCards((prev) => [
      ...prev,
      ...ids.filter((id) => !prev.some((c) => c.propertyId === id)).map(makePanelCardFromPropertyId),
    ]);
    dissolveRelationsGroup();
  };
  // property-group menu: eye toggles move a property to/from the panel
  const hideGroupProperty = (propertyId: string) =>
    setPanelStagingCards((prev) =>
      prev.some((c) => c.propertyId === propertyId) ? prev : [...prev, makePanelCardFromPropertyId(propertyId)],
    );
  const showGroupProperty = (propertyId: string) =>
    setPanelStagingCards((prev) => prev.filter((c) => c.propertyId !== propertyId));
  const moveGroupToPanel = () => {
    const ids = propertyEntries.map(([id]) => id).filter((id) => !panelPropertyIds.has(id));
    setPanelStagingCards((prev) => [...prev, ...ids.map(makePanelCardFromPropertyId)]);
  };
  const removeStagingCard = (card: PanelCard) =>
    setPanelStagingCards((prev) => prev.filter((c) => c.id !== card.id));
  const moveStagingCardToPage = (card: PanelCard) => {
    removeStagingCard(card);
    setPanelCards((prev) => (prev.some((c) => c.id === card.id) ? prev : [...prev, card]));
    setTopPreviewOrder((prev) => (prev.includes(card.id) ? prev : [...prev, card.id]));
  };
  // reorders only among the bottom cards; the list also holds entries that
  // aren't drawn as cards (heading, property group)
  const isBottomModule = (m: Module) => m.id === "discussions" || m.id === "content";
  const moveModule = (id: string, dir: -1 | 1) =>
    setModules((prev) => {
      const bottom = prev.filter(isBottomModule);
      const i = bottom.findIndex((m) => m.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= bottom.length) return prev;
      const next = [...bottom];
      const a = next[i];
      const b = next[j];
      if (!a || !b) return prev;
      next[i] = b;
      next[j] = a;
      return [...prev.filter((m) => !isBottomModule(m)), ...next];
    });

  const relationsReorder = useSlidingReorder<string>({
    keys: relationsGroupIds ?? [],
    onReorder: (from, to) =>
      setRelationsGroupIds((prev) => {
        if (!prev) return prev;
        const next = [...prev];
        const [moved] = next.splice(from, 1);
        if (moved === undefined) return prev;
        next.splice(to, 0, moved);
        return next;
      }),
  });

  // pinning evicts the property from any card/staging slot so it never renders twice
  const pinProperty = (propertyId: string) => {
    setPinnedIds((prev) => (prev.includes(propertyId) ? prev : [...prev, propertyId]));
    setPanelCards((prev) => prev.filter((card) => card.propertyId !== propertyId));
    setPanelStagingCards((prev) => prev.filter((card) => card.propertyId !== propertyId));
    setTopPreviewOrder((prev) => prev.filter((id) => id !== propertyId));
  };
  const unpinProperty = (propertyId: string) =>
    setPinnedIds((prev) => prev.filter((id) => id !== propertyId));
  const pinnedReorder = useSlidingReorder<string>({
    keys: pinnedIds,
    onReorder: (from, to) =>
      setPinnedIds((prev) => {
        const next = [...prev];
        const [moved] = next.splice(from, 1);
        if (moved === undefined) return prev;
        next.splice(to, 0, moved);
        return next;
      }),
  });

  const propertyIconFor = (propertyId: string) => {
    const prop = boardProperties?.[propertyId] as { type?: string; icon?: string } | undefined;
    return prop?.icon
      ? renderIcon(prop.icon, { style: { width: 16, height: 16 } })
      : renderTypeIcon(prop?.type || "text");
  };

  const pinnedPreviewContent = pinnedIds.length > 0 ? (
    <HorizontalScrollStrip className="mt-3 px-2">
    <div className="flex w-max min-w-full flex-nowrap items-start gap-x-5">
      {pinnedIds.map((id) => {
        const prop = boardProperties?.[id];
        if (!prop) return null;
        const labelsOff = !settings.pinnedPropertyLabels;
        if (labelsOff && isEmptyPropertyValue(prop.type, previewDb[id])) {
          return (
            <div key={id} className="flex shrink-0 items-center gap-1.5 px-1 py-1.5 text-sm text-gray-400 dark:text-gray-500">
              <span className="shrink-0">{propertyIconFor(id)}</span>
              <span className="truncate">{prop.name}</span>
            </div>
          );
        }
        return (
          <div key={id} className={`flex shrink-0 flex-col ${prop.type === "text" ? "max-w-[250px]" : "max-w-[320px]"}`}>
            {!labelsOff && (
              <div className="flex items-center gap-1.5 px-1 py-0.5 text-[13px] font-medium text-gray-600 dark:text-gray-400">
                <span className="shrink-0 text-gray-500">{propertyIconFor(id)}</span>
                <span className="truncate">{prop.name}</span>
              </div>
            )}
            <div className={`flex min-h-[24px] items-center px-1 [&>*]:truncate ${prop.type === "text" ? "" : "[&>*]:!w-auto [&>*]:max-w-[300px]"}`}>
              <LayoutEditorPropertyValuePreview
                property={prop as BoardProperty}
                value={previewDb[id]}
                formulaError={previewFormulaErrors?.[id]}
                resolveRelationTitle={resolveRelationTitle}
              />
            </div>
          </div>
        );
      })}
    </div>
    </HorizontalScrollStrip>
  ) : null;

  const hasVisibleProperties = useMemo(
    () => propertyEntries.some(([id]) => !panelPropertyIds.has(id)),
    [propertyEntries, panelPropertyIds],
  );

  const relationsPreviewContent = (
    <div className="px-3 pb-2">
      <div className="mb-1.5 text-sm font-medium text-gray-600 dark:text-gray-400">Relations</div>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {(relationsGroupIds ?? []).map((id) => {
          const prop = boardProperties?.[id];
          if (!prop) return null;
          return (
            <div key={id} className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <span className="shrink-0">{propertyIconFor(id)}</span>
              <span>Add {prop.name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );

  const propertiesPreviewContent = useMemo(() => {
    if (!boardProperties || Object.keys(boardProperties).length === 0) {
      return <div className="px-3 py-4 text-sm text-muted-foreground">No properties in this database.</div>;
    }
    if (!hasVisibleProperties) {
      return <div className="px-3 pb-3 text-sm text-muted-foreground">Empty</div>;
    }
    return (
      <LayoutEditorPropertiesPreviewRows
        boardProperties={boardProperties}
        propertyOrder={resolvedPropertyOrder}
        databaseProperties={previewDb}
        formulaErrors={previewFormulaErrors}
        hiddenPropertyIds={[...panelPropertyIds]}
        resolveRelationTitle={resolveRelationTitle}
      />
    );
  }, [boardProperties, hasVisibleProperties, panelPropertyIds, resolvedPropertyOrder, previewDb, previewFormulaErrors]);

  const existingProperties = propertyEntries
    .filter(([id]) => !panelPropertyIds.has(id))
    .map(([id, prop]) => ({
    id,
    name: prop?.name || "Property",
    type: getPropertyTypeMeta(prop?.type || "text").label,
    icon: renderTypeIcon(prop?.type || "text"),
    }));

  // Relation needs a target datasource, which this quick-add can't ask for —
  // creating one here would leave it unlinked.
  const newPropertyTemplates = PROPERTY_TYPES.filter((t) => t.type !== "relation").map((t) => ({
    id: t.type,
    name: t.label,
    type: t.type,
    icon: renderTypeIcon(t.type),
  }));

  const normalizedSearch = panelSearch.trim().toLowerCase();
  const filteredExistingProperties = normalizedSearch
    ? existingProperties.filter((p) => p.name.toLowerCase().includes(normalizedSearch) || p.type.toLowerCase().includes(normalizedSearch))
    : existingProperties;
  const filteredNewTemplates = normalizedSearch
    ? newPropertyTemplates.filter((t) => t.name.toLowerCase().includes(normalizedSearch))
    : newPropertyTemplates;

  const makePanelCardFromPropertyId = (propertyId: string): PanelCard => {
    const prop = boardProperties?.[propertyId];
    const type = getPropertyTypeMeta(prop?.type || "text");
    return {
      id: propertyId,
      propertyId,
      name: prop?.name || "Property",
      type: type.label,
      icon: renderTypeIcon(prop?.type || "text"),
    };
  };

  const initialPanelsHydratedRef = useRef(false);
  useEffect(() => {
    if (initialPanelsHydratedRef.current) return;
    initialPanelsHydratedRef.current = true;

    const visible = (initialSettings?.visiblePanels ?? []) as PagePanel[];
    const hidden = (initialSettings?.hiddenPanels ?? []) as PagePanel[];
    if (visible.length === 0 && hidden.length === 0) return;

    const loadedPanelCards: PanelCard[] = [];
    const loadedTopOrder: string[] = [];

    const headingPanel = visible.find((panel) => panel.type === "heading");
    setPinnedIds((headingPanel?.propertyIds ?? []).filter((id) => !!boardProperties?.[id]));

    for (const panel of visible) {
      if (panel.type === "relations_group") {
        const ids = (panel.propertyIds ?? []).filter((id) => !!boardProperties?.[id]);
        if (ids.length) {
          setRelationsGroupIds(ids);
          loadedTopOrder.push("relations");
        }
        continue;
      }
      if (panel.type !== "property_group" && panel.type !== "property_single") continue;
      const propIds = panel.propertyIds ?? [];
      if (panel.id === "property-group") {
        loadedTopOrder.push("properties");
        continue;
      }
      const propId = propIds[0];
      if (!propId) continue;
      loadedPanelCards.push(makePanelCardFromPropertyId(propId));
      loadedTopOrder.push(propId);
    }

    const loadedHiddenCards: PanelCard[] = hidden
      .filter((panel) => panel.type === "property_group" || panel.type === "property_single")
      .map((panel) => panel.propertyIds?.[0])
      .filter((propertyId): propertyId is string => !!propertyId)
      .map((propertyId) => makePanelCardFromPropertyId(propertyId));

    setPanelCards(loadedPanelCards);
    setPanelStagingCards(loadedHiddenCards);
    if (loadedTopOrder.length > 0) {
      setTopPreviewOrder(loadedTopOrder.includes("properties") ? loadedTopOrder : ["properties", ...loadedTopOrder]);
    }
  }, [initialSettings, boardProperties]);

  const addPanelCard = (
    name: string,
    type: string,
    icon: React.ReactNode,
    propertyId?: string,
    propertyType?: string,
  ) => {
    if (propertyId && panelPropertyIds.has(propertyId)) {
      setAddToPanelMode(false);
      setPanelSearch("");
      return;
    }
    setPanelStagingCards((prev) => [
      ...prev,
      { id: `${Date.now()}-${prev.length}`, name, type, icon, propertyId, propertyType },
    ]);
    setAddToPanelMode(false);
    setPanelSearch("");
  };

  const addPreviewCard = (
    name: string,
    type: string,
    icon: React.ReactNode,
    propertyId?: string,
    propertyType?: string,
  ) => {
    if (propertyId && panelPropertyIds.has(propertyId)) {
      setAddToPreviewMode(false);
      setPanelSearch("");
      return;
    }
    const newCard: PanelCard = {
      id: `${Date.now()}-preview`,
      name,
      type,
      icon,
      propertyId,
      propertyType,
    };
    setPanelCards((prev) => [...prev, newCard]);
    setAddToPreviewMode(false);
    setPanelSearch("");
  };

  useEffect(() => {
    if (!addToPanelMode && !addToPreviewMode) return;
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (settingsPanelRef.current?.contains(target)) return;
      if (addToPanelTriggerRef.current?.contains(target)) return;
      setAddToPanelMode(false);
      setAddToPreviewMode(false);
      setPanelSearch("");
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [addToPanelMode, addToPreviewMode]);

  const moveStagingCardToPreview = (cardId: string) => {
    const card = panelStagingCards.find((c) => c.id === cardId);
    if (!card) return;

    setPanelStagingCards((prev) => prev.filter((c) => c.id !== cardId));
    setPanelCards((previewPrev) => {
      if (previewPrev.some((c) => c.id === card.id)) {
        return previewPrev;
      }
      if (card.propertyId && previewPrev.some((c) => c.propertyId === card.propertyId)) {
        return previewPrev;
      }
      return [...previewPrev, card];
    });
  };

  // keep the order in sync with the cards; the group token always stays (an
  // empty group still shows its container)
  useEffect(() => {
    const panelIds = panelCards.map((c) => c.id);
    setTopPreviewOrder((prev) => {
      const filtered = prev.filter(
        (id) => id === "properties" || id === "relations" || panelIds.includes(id),
      );
      const base = filtered.includes("properties") ? filtered : ["properties", ...filtered];
      const existing = new Set(base);
      return [...base, ...panelIds.filter((id) => !existing.has(id))];
    });
  }, [panelCards]);

  const topPreviewItems = useMemo(() => {
    const byId = new Map(panelCards.map((c) => [c.id, c]));
    return topPreviewOrder
      .map((id) => {
        if (id === "properties") return { kind: "properties" as const, id };
        if (id === "relations") return relationsGroupIds ? { kind: "relations" as const, id } : null;
        const card = byId.get(id);
        return card ? { kind: "panel" as const, id, card } : null;
      })
      .filter(Boolean) as Array<
        | { kind: "properties"; id: string }
        | { kind: "relations"; id: string }
        | { kind: "panel"; id: string; card: PanelCard }
      >;
  }, [panelCards, topPreviewOrder, relationsGroupIds]);

  const handleTopPreviewDrop = (i: number) => {
    if (topPreviewDragIdx === null || topPreviewDragIdx === i) {
      setTopPreviewDragIdx(null);
      setTopPreviewDragOver(null);
      return;
    }
    const nextOrder = [...topPreviewOrder];
    const [moved] = nextOrder.splice(topPreviewDragIdx, 1);
    if (!moved) {
      setTopPreviewDragIdx(null);
      setTopPreviewDragOver(null);
      return;
    }
    nextOrder.splice(i, 0, moved);
    setTopPreviewOrder(nextOrder);
    setPanelCards((prev) => {
      const cardById = new Map(prev.map((c) => [c.id, c]));
      return nextOrder
        .map((id) => (id === "properties" ? null : cardById.get(id)))
        .filter(Boolean) as PanelCard[];
    });
    setTopPreviewDragIdx(null);
    setTopPreviewDragOver(null);
  };

  const modulesBeforePanel = [
    modules.find((m) => m.id === "heading"),
  ].filter(Boolean) as Module[];
  const modulesAfterPanel = modules.filter((m) => {
    if (m.id === "discussions") return settings.pageDiscussions;
    return m.id === "content";
  });

  const topItemIds = topPreviewItems.map((item) => item.id);
  const topReorder = useSlidingReorder<string>({
    keys: topItemIds,
    gap: 12,
    onReorder: (from, to) => {
      const moved = topItemIds[from];
      const target = topItemIds[to];
      if (moved === undefined || target === undefined) return;
      setTopPreviewOrder((prev) => {
        const rest = prev.filter((id) => id !== moved);
        const at = rest.indexOf(target) + (to > from ? 1 : 0);
        return [...rest.slice(0, at), moved, ...rest.slice(at)];
      });
    },
  });
  const moduleIds = modulesAfterPanel.map((m) => m.id);
  const moduleReorder = useSlidingReorder<string>({
    keys: moduleIds,
    gap: 12,
    onReorder: (from, to) => {
      const id = moduleIds[from];
      if (id === undefined) return;
      const dir = to > from ? 1 : -1;
      for (let k = 0; k < Math.abs(to - from); k++) moveModule(id, dir);
    },
  });
  const groupOrderIds = useMemo(
    () => resolvedPropertyOrder.filter((id) => !!boardProperties?.[id] && !panelPropertyIds.has(id)),
    [resolvedPropertyOrder, boardProperties, panelPropertyIds],
  );
  const groupReorder = useSlidingReorder<string>({
    keys: groupOrderIds,
    onReorder: (from, to) => {
      const moved = groupOrderIds[from];
      const target = groupOrderIds[to];
      if (moved === undefined || target === undefined) return;
      const rest = resolvedPropertyOrder.filter((id) => id !== moved);
      const at = rest.indexOf(target) + (to > from ? 1 : 0);
      setLocalPropertyOrder([...rest.slice(0, at), moved, ...rest.slice(at)]);
    },
  });

  useEffect(() => {
    if (!settings.pageDiscussions) return;
    setModules((prev) => {
      if (prev.some((m) => m.id === "discussions")) return prev;
      const discussionsModule = ALL_MODULES.find((m) => m.id === "discussions");
      if (!discussionsModule) return prev;
      const contentIdx = prev.findIndex((m) => m.id === "content");
      if (contentIdx === -1) return [...prev, discussionsModule];
      const next = [...prev];
      next.splice(contentIdx, 0, discussionsModule);
      return next;
    });
  }, [settings.pageDiscussions]);

  const renderPanelCardValue = (card: PanelCard) => {
    if (!card.propertyId || !boardProperties?.[card.propertyId]) {
      return <span className="text-sm text-muted-foreground">Empty</span>;
    }
    const prop = boardProperties[card.propertyId] as BoardProperty;
    const rawValue = previewDb[card.propertyId];
    return (
      <LayoutEditorPropertyValuePreview
        property={prop}
        value={rawValue}
        formulaError={previewFormulaErrors?.[card.propertyId]}
        resolveRelationTitle={resolveRelationTitle}
      />
    );
  };

  const buildPayload = (
    cardsForPayload: PanelCard[] = panelCards,
    stagingForPayload: PanelCard[] = panelStagingCards,
    extraPropertyIds: string[] = [],
  ): CustomLayoutSavePayload => {
    const usedPropertyIds = new Set([
      ...[...cardsForPayload, ...stagingForPayload]
        .map((card) => card.propertyId)
        .filter((propertyId): propertyId is string => !!propertyId),
      ...pinnedIds,
      ...(relationsGroupIds ?? []),
    ]);
    const orderWithExtras = [...resolvedPropertyOrder];
    for (const id of extraPropertyIds) {
      if (!orderWithExtras.includes(id)) orderWithExtras.push(id);
    }
    const visiblePropertyIds = orderWithExtras.filter(
      (id) => !usedPropertyIds.has(id) && (!!boardProperties?.[id] || extraPropertyIds.includes(id)),
    );
    const pinnedForPayload = pinnedIds.filter((id) => !!boardProperties?.[id]);
    const visiblePanelsPayload: PagePanel[] = [
      { id: "heading", type: "heading", ...(pinnedForPayload.length ? { propertyIds: pinnedForPayload } : {}) },
    ];

    const relationIdsForPayload = (relationsGroupIds ?? []).filter((id) => !!boardProperties?.[id]);
    const topPanels = topPreviewOrder.filter(
      (id) =>
        id === "properties" ||
        (id === "relations" && relationIdsForPayload.length > 0) ||
        cardsForPayload.some((card) => card.id === id),
    );
    for (const item of topPanels) {
      if (item === "properties") {
        // an empty group stays in the layout (renders "Empty" / its add button)
        visiblePanelsPayload.push({
          id: "property-group",
          type: "property_group",
          propertyIds: visiblePropertyIds,
        });
        continue;
      }
      if (item === "relations") {
        visiblePanelsPayload.push({
          id: "relations-group",
          type: "relations_group",
          propertyIds: relationIdsForPayload,
        });
        continue;
      }
      const card = cardsForPayload.find((c) => c.id === item);
      const propId = card?.propertyId;
      if (!propId) continue;
      visiblePanelsPayload.push({
        id: propId,
        type: "property_single",
        propertyIds: [propId],
      });
    }

    const bottomPanelsInOrder = modules
      .filter((m) => m.id === "discussions" || m.id === "content")
      .filter((m) => (m.id === "discussions" ? settings.pageDiscussions : true));

    for (const panel of bottomPanelsInOrder) {
      if (panel.id === "discussions") {
        visiblePanelsPayload.push({ id: "page-discussions", type: "page_discussions" });
        continue;
      }
      if (panel.id === "content") {
        visiblePanelsPayload.push({ id: "content", type: "content" });
      }
    }

    const hiddenPanelsPayload: PagePanel[] = stagingForPayload
      .map((card) => card.propertyId)
      .filter((propertyId): propertyId is string => !!propertyId)
      .map((propertyId) => ({
        id: propertyId,
        type: "property_single" as const,
        propertyIds: [propertyId],
      }));

    return {
      pageSettings: {
        structure: settings.structure,
        inlineComments: settings.inlineComments,
        pageDiscussions: settings.pageDiscussions,
        propertyIcons: settings.propertyIcons,
        pinnedPropertyLabels: settings.pinnedPropertyLabels,
        fullWidth: settings.fullWidth,
        visiblePanels: visiblePanelsPayload,
        hiddenPanels: hiddenPanelsPayload,
      },
    };
  };

  /**
   * For every card that was added from a "Create new property" template
   * (carries `propertyType` but no `propertyId`), create the property in
   * the datasource and return new card arrays with the freshly-assigned IDs.
   * Also returns the list of new propertyIds so callers can update the
   * board's property order.
   */
  const materializeNewProperties = async (): Promise<{
    cards: PanelCard[];
    staging: PanelCard[];
    newPropertyIds: string[];
  }> => {
    const cards = [...panelCards];
    const staging = [...panelStagingCards];
    const newPropertyIds: string[] = [];

    if (!dataSourceId || !workspaceId) {
      // Cannot create properties without datasource context — drop the
      // template-only cards from the payload by clearing their propertyType.
      const clear = (arr: PanelCard[]) =>
        arr.map((c) => (c.propertyType && !c.propertyId ? { ...c, propertyType: undefined } : c));
      return { cards: clear(cards), staging: clear(staging), newPropertyIds };
    }

    const startingDS = getDataSource(dataSourceId);
    const dsProperties = startingDS?.properties ?? boardProperties ?? {};
    const hasDefaultOfType = (type: string) =>
      Object.values(dsProperties).some((p) => p?.type === type && p.default === true);

    const createOne = async (card: PanelCard): Promise<PanelCard> => {
      if (!card.propertyType || card.propertyId) return card;
      const propertyId = generatePropertyId();
      const schema: BoardProperty = {
        name: card.propertyType,
        type: card.propertyType as BoardProperty["type"],
        default: !hasDefaultOfType(card.propertyType),
        showProperty: true,
      };

      // Optimistically add the property to the datasource in React context
      // BEFORE the IDB mutation. createPropertyMutation only writes to IDB,
      // and there is no live-query consumer that pushes IDB updates back into
      // the boardContext, so without this step the new property never reaches
      // the UI even though the layout payload references its propertyId.
      const latestDS = getDataSource(dataSourceId);
      if (latestDS) {
        updateDataSource(dataSourceId, {
          properties: { ...latestDS.properties, [propertyId]: schema },
        });
      }

      await createPropertyMutation(
        dataSourceId,
        propertyId,
        schema,
        {
          propertyId,
          dataSourceId,
          blockId: boardId,
          name: card.propertyType,
          type: card.propertyType,
          options: [],
        },
        workspaceId,
      );
      newPropertyIds.push(propertyId);
      return { ...card, propertyId };
    };

    for (let i = 0; i < cards.length; i++) {
      const card = cards[i];
      if (card) cards[i] = await createOne(card);
    }
    for (let i = 0; i < staging.length; i++) {
      const card = staging[i];
      if (card) staging[i] = await createOne(card);
    }

    return { cards, staging, newPropertyIds };
  };

  const handleSave = async () => {
    try {
      const { cards, staging, newPropertyIds } = await materializeNewProperties();

      // Persist the resolved cards so the editor's local UI keeps the freshly
      // assigned propertyIds if the user re-opens it without a refresh.
      if (newPropertyIds.length > 0) {
        setPanelCards(cards);
        setPanelStagingCards(staging);
      }
      if (newPropertyIds.length > 0 || localPropertyOrder) {
        // Append new propertyIds to the board's property order so the property
        // group section shows them in the same place as if the user had added
        // them through "+ Add property".
        const existing =
          localPropertyOrder ??
          (contextPropertyOrder?.[propertyOrderKey] && contextPropertyOrder[propertyOrderKey].length > 0
            ? contextPropertyOrder[propertyOrderKey]
            : Object.keys(boardProperties || {}));
        const merged = [...existing];
        for (const id of newPropertyIds) {
          if (!merged.includes(id)) merged.push(id);
        }
        setPropertyOrder(propertyOrderKey, merged);
      }

      const payload = buildPayload(cards, staging, newPropertyIds);
      // Layout is always applied datasource-wide (every page in the datasource).
      await onSaveLayout?.("all_pages", payload);
      toast.success("Layout applied to all pages");
      onClose();
    } catch (error) {
      console.error("Failed to save custom layout:", error);
      toast.error("Failed to save layout");
    }
  };

  const buildResetPayload = (): CustomLayoutSavePayload => {
    const allPropertyIds = resolvedPropertyOrder.filter((id) => !!boardProperties?.[id]);
    return {
      pageSettings: {
        structure: "simple",
        inlineComments: "default",
        pageDiscussions: true,
        propertyIcons: true,
        pinnedPropertyLabels: true,
        fullWidth: false,
        visiblePanels: [
          { id: "heading", type: "heading" },
          {
            id: "property-group",
            type: "property_group",
            propertyIds: allPropertyIds,
          },
          { id: "page-discussions", type: "page_discussions" },
          { id: "content", type: "content" },
        ],
        hiddenPanels: [],
      },
    };
  };

  const handleResetToOriginalLayout = async () => {
    try {
      await onSaveLayout?.("all_pages", buildResetPayload());
      toast.success("Page layout reset");
      setShowResetLayoutModal(false);
      onClose();
    } catch (error) {
      console.error("Failed to reset page layout:", error);
      toast.error("Failed to reset layout");
    }
  };

  if (typeof window === "undefined") return null;

  const layoutDatabaseTitle = (databaseTitle ?? pageName) || "Database";

  return createPortal(
    <div
      className={layoutEditorRootClass}
      style={{ position: "fixed", inset: 0, zIndex: 99999 }}
    >
      {/* ── Header: centered source title + preview, corner actions ─ */}
      <header className="relative z-[1] shrink-0 bg-[#fbfbfc] dark:bg-[#191919]">
        <div className="relative mx-4 my-2 min-h-[52px]">
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center">
            <button
              type="button"
              className="pointer-events-auto inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[13px] font-medium text-foreground hover-component"
            >
              <SlidersHorizontal className="h-4 w-4 shrink-0 text-muted-foreground" />
              Page settings
            </button>
          </div>

          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              id="layout-cancel-btn"
              className="pointer-events-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-[13px] font-medium text-foreground hover-component"
            >
              <X className="h-3.5 w-3.5" /> Cancel
            </button>
            <div className="pointer-events-auto relative inline-flex h-8">
              <div className="inline-flex h-8 overflow-hidden rounded-md">
                <button
                  type="button"
                  id="layout-apply-btn"
                  onClick={() => { void handleSave(); }}
                  className="flex items-center gap-1.5 border-0 bg-[#2783de] px-2 ps-2 text-[14px] font-medium leading-tight text-white hover:opacity-95"
                >
                  <Check className="h-4 w-4 shrink-0" strokeWidth={2.5} />
                  Apply to all pages
                </button>
                <button
                  type="button"
                  onClick={() => setSaveOpen((p) => !p)}
                  aria-label="More save options"
                  className="flex w-6 shrink-0 items-center justify-center border-0 border-l border-white/25 bg-[#2783de] text-white hover:opacity-95"
                >
                  <ChevronDown className="h-4 w-4" strokeWidth={2} />
                </button>
              </div>
              {saveOpen && (
                <SelfAnchoredPopover
                  placement="bottom-end"
                  onClose={() => setSaveOpen(false)}
                  className="w-64 rounded-lg border border-border bg-popover py-1 shadow-xl"
                >
                  <button
                    type="button"
                    onClick={() => {
                      setSaveOpen(false);
                      setShowResetLayoutModal(true);
                    }}
                    className="w-full px-3 py-2 text-left text-[13px] text-red-600 hover-component dark:text-red-400"
                  >
                    Restore to original page layout
                  </button>
                </SelfAnchoredPopover>
              )}
            </div>
          </div>

          <div className="flex flex-col items-center justify-center px-28 pt-0.5">
            <div className="flex max-w-[400px] items-center justify-center gap-1 text-[15px] font-medium text-foreground">
              <Database className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
              <span className="truncate">{layoutDatabaseTitle}</span>
            </div>
            <button
              ref={pagePickerTriggerRef}
              type="button"
              aria-expanded={pagePickerOpen}
              onClick={() => (pagePickerOpen ? closePagePicker() : setPagePickerOpen(true))}
              className="pointer-events-auto mt-0.5 inline-flex max-w-[min(400px,100%)] items-center gap-1.5 rounded-md px-2 py-[3px] text-[12px] font-medium text-muted-foreground hover-component"
            >
              <span className="shrink-0">Preview:</span>
              <span className={`min-w-0 truncate ${hasPreviewTitle ? "text-foreground" : "text-muted-foreground"}`}>{previewTitle}</span>
              <ChevronDown className="h-3 w-3 shrink-0 opacity-70" />
            </button>
            {pagePickerOpen && (
              <AnchoredPopover
                anchor={pagePickerTriggerRef}
                onClose={closePagePicker}
                placement="bottom"
                offsetPx={6}
                className="z-[100010] flex max-h-[70vh] w-[min(300px,calc(100vw-24px))] min-w-[180px] flex-col overflow-hidden rounded-lg border border-component bg-modal p-0 text-popover-foreground shadow-md outline-none"
              >
                <div
                  style={{ display: "contents" }}
                  onKeyDown={(e) => {
                    if (e.key === "Escape" && !e.defaultPrevented) {
                      e.stopPropagation();
                      closePagePicker();
                    }
                  }}
                >
                <div className="border-b border-border p-2">
                  <div className="flex h-7 items-center rounded-md border border-border bg-muted/30 px-1.5 dark:bg-muted/20">
                    <Search className="mx-1 h-3.5 w-3.5 shrink-0 opacity-50" aria-hidden />
                    <input
                      value={pageSearchQuery}
                      onChange={(e) => setPageSearchQuery(e.target.value)}
                      placeholder="Search pages…"
                      className="h-full min-w-0 flex-1 bg-transparent text-[13px] outline-none placeholder:text-muted-foreground"
                    />
                  </div>
                </div>
                <div className="max-h-[min(400px,55vh)] overflow-y-auto py-1 clean-scrollbar" role="menu">
                  {filteredPickerPages.length === 0 ? (
                    <p className="px-3 py-4 text-center text-[13px] text-muted-foreground">No matching pages</p>
                  ) : (
                    filteredPickerPages.map((p) => {
                      const pageValue = p.value as { title?: string; icon?: string };
                      const titleRaw = String(pageValue?.title ?? "").trim();
                      const title = titleRaw || "New page";
                      const icon = String(pageValue?.icon ?? "").trim();
                      const selected = p._id === previewPageId;
                      return (
                        <button
                          key={p._id}
                          type="button"
                          role="menuitem"
                          className={`flex w-full items-center gap-2 px-2 py-1.5 text-left text-[13px] hover-component ${selected ? "bg-muted/80" : ""}`}
                          onClick={() => {
                            setPreviewPageId(p._id);
                            setPagePickerOpen(false);
                            setPageSearchQuery("");
                          }}
                        >
                          {icon ? (
                            <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-[14px] leading-none" role="img" aria-label="Page icon">
                              {renderIcon(icon, { style: { width: 14, height: 14 } })}
                            </span>
                          ) : (
                            <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                          )}
                          <span className="min-w-0 flex-1 truncate text-foreground">{title}</span>
                          {selected ? <Check className="h-4 w-4 shrink-0 text-foreground" strokeWidth={2.5} /> : null}
                        </button>
                      );
                    })
                  )}
                </div>
                </div>
              </AnchoredPopover>
            )}
          </div>
        </div>
      </header>

      {/* ── Body ─────────────────────────────────────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden">

        {/* Preview canvas */}
        <div className="relative min-w-[700px] flex-1 overflow-hidden pb-0 ps-6 pt-5 pe-6">
          <div className="mx-auto h-full w-full">
            {/* Shared shell: left preview + add-to-panel column */}
            <div className={`flex h-full max-h-[calc(100vh-140px)] overflow-hidden transition-shadow ${layoutEditorShellClass}`}>
              <div className="flex-1 overflow-y-auto bg-white dark:bg-[#202020] clean-scrollbar">
                <div className="h-0 sticky top-0 z-20" />
                <div 
                  onDragOver={(e) => {
                    if (draggingStagingCardId) {
                      e.preventDefault();
                      setIsPreviewDropActive(true);
                    }
                  }}
                  onDragLeave={() => setIsPreviewDropActive(false)}
                  onDrop={(e) => {
                    if (draggingStagingCardId) {
                      e.preventDefault();
                      moveStagingCardToPreview(draggingStagingCardId);
                      setDraggingStagingCardId(null);
                      setIsPreviewDropActive(false);
                    }
                  }}
                  className={`pt-7 pb-4 flex flex-col gap-3 mx-auto w-full transition-all duration-300 ${settings.fullWidth ? "max-w-full px-12" : "max-w-[720px] px-8"}`}
                >
                  {modulesBeforePanel.map((mod) => {
                    const i = modules.findIndex((m) => m.id === mod.id);
                    return (
                    <ModuleCard
                      key={mod.id} mod={mod}
                      dragging={dragIdx === i}
                      dragOver={dragOver === i && dragIdx !== i}
                      onDragStart={() => handleDragStart(i)}
                      onDragOver={(e) => handleDragOver(i, e)}
                      onDragLeave={() => setDragOver(null)}
                      onDrop={() => handleDrop(i)}
                      onDragEnd={() => { setDragIdx(null); setDragOver(null); }}
                      onRemove={() => setModules(ms => ms.filter((_, idx) => idx !== i))}
                      propertiesPreviewContent={propertiesPreviewContent}
                      pageTitle={previewTitleRaw}
                      pageIcon={previewIcon}
                      active={mod.id === "heading" && headingConfigOpen}
                      configureRef={mod.id === "heading" ? headingMenuBtnRef : undefined}
                      onConfigure={mod.id === "heading" ? () => setHeadingConfigOpen(true) : undefined}
                      pinnedPreviewContent={mod.id === "heading" ? pinnedPreviewContent : undefined}
                    />
                  );})}

                  {headingConfigOpen && (
                    <AnchoredPopover
                      anchor={headingMenuBtnRef}
                      placement="bottom-end"
                      offsetPx={6}
                      zIndex={100010}
                      onClose={() => { setHeadingConfigOpen(false); setHeadingSearch(""); }}
                      className="border-component w-[300px] rounded-lg border bg-modal p-2 text-popover-foreground shadow-md"
                    >
                      {(() => {
                        const q = headingSearch.trim().toLowerCase();
                        const matches = (id: string) =>
                          !q || String(boardProperties?.[id]?.name ?? "").toLowerCase().includes(q);
                        const pinnedRows = pinnedIds.filter((id) => !!boardProperties?.[id] && matches(id));
                        // sidebar order first, then any property it doesn't know about yet
                        const allIds = [
                          ...resolvedPropertyOrder,
                          ...Object.keys(boardProperties ?? {}).filter((id) => !resolvedPropertyOrder.includes(id)),
                        ];
                        const unpinnedRows = allIds.filter(
                          (id) => !!boardProperties?.[id] && !pinnedIds.includes(id) && matches(id),
                        );
                        // no reorder while search hides rows — positions would be ambiguous
                        const canReorder = !q && pinnedIds.length > 1;
                        const row = (id: string, pinned: boolean) => (
                          <div className="group flex items-center gap-2 rounded-md px-2 py-1.5 hover-component">
                            <span className="relative flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground">
                              <span className={pinned && canReorder ? "group-hover:opacity-0" : ""}>{propertyIconFor(id)}</span>
                              {pinned && canReorder && (
                                <GripVertical
                                  aria-label="Drag to reorder"
                                  onPointerDown={pinnedReorder.startDrag(id)}
                                  className="absolute inset-0 h-4 w-4 cursor-grab opacity-0 group-hover:opacity-100 active:cursor-grabbing"
                                />
                              )}
                            </span>
                            <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                              {boardProperties?.[id]?.name || "Property"}
                            </span>
                            <button
                              type="button"
                              aria-label={pinned ? "Unpin property" : "Pin property"}
                              onClick={() => (pinned ? unpinProperty(id) : pinProperty(id))}
                              className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground"
                            >
                              {pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                            </button>
                          </div>
                        );
                        return (
                          <div className="space-y-3">
                            <p className="px-1 text-[13px] font-medium text-foreground">Heading</p>
                            <div className="border-component flex h-8 items-center gap-2 rounded-md border px-2">
                              <Search className="h-4 w-4 text-muted-foreground" />
                              <input
                                value={headingSearch}
                                onChange={(e) => setHeadingSearch(e.target.value)}
                                placeholder="Search properties…"
                                className="w-full bg-transparent text-sm outline-none"
                              />
                            </div>
                            <div ref={pinnedReorder.scrollerRef} className="clean-scrollbar max-h-[min(360px,50vh)] space-y-3 overflow-y-auto">
                            <div>
                              <p className="px-2 pb-1 text-[11px] font-semibold text-muted-foreground">Pinned properties</p>
                              <div>
                                {pinnedRows.length > 0
                                  ? pinnedRows.map((id, index) => (
                                      <div key={id} {...pinnedReorder.itemProps(id, index)}>
                                        {row(id, true)}
                                      </div>
                                    ))
                                  : <p className="px-2 py-1 text-[13px] text-muted-foreground/60">No pinned properties</p>}
                              </div>
                            </div>
                            <div>
                              <p className="px-2 pb-1 text-[11px] font-semibold text-muted-foreground">Unpinned properties</p>
                              {unpinnedRows.length > 0
                                ? unpinnedRows.map((id) => <div key={id}>{row(id, false)}</div>)
                                : <p className="px-2 py-1 text-[13px] text-muted-foreground/60">No properties</p>}
                            </div>
                            </div>
                            <div className="border-component border-t pt-2">
                              <p className="px-2 pb-1 text-[11px] font-semibold text-muted-foreground">Options</p>
                              <div className="flex items-center justify-between gap-2 px-2 py-1.5">
                                <label htmlFor="layout-pinned-labels" className="cursor-pointer text-[13px] text-foreground">Property labels</label>
                                <Toggle
                                  id="layout-pinned-labels"
                                  checked={settings.pinnedPropertyLabels}
                                  onChange={(v) => set("pinnedPropertyLabels", v)}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </AnchoredPopover>
                  )}

                  <div ref={topReorder.scrollerRef} className="layout-content mb-3 flex w-full flex-col gap-3">
                    {topPreviewItems.map((item, i) => {
                      const slide = topReorder.itemProps(item.id, i);
                      return (
                      <div
                        key={item.id}
                        ref={slide.ref}
                        style={slide.style}
                        draggable
                        onDragStart={() => setTopPreviewDragIdx(i)}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setTopPreviewDragOver(i);
                        }}
                        onDrop={() => handleTopPreviewDrop(i)}
                        onDragLeave={() => setTopPreviewDragOver(null)}
                        onDragEnd={() => {
                          setTopPreviewDragIdx(null);
                          setTopPreviewDragOver(null);
                        }}
                        className={`flex w-full flex-col cursor-grab ${slide.className}`}
                      >
                        {item.kind === "properties" || item.kind === "relations" ? (
                          <GroupCard
                            title={item.kind === "properties" ? "Property group" : "Relations group"}
                            dragging={topPreviewDragIdx === i}
                            dragOver={topPreviewDragOver === i && topPreviewDragIdx !== i}
                            active={openItemMenu === item.id}
                            onDragStart={() => setTopPreviewDragIdx(i)}
                            onDragOver={(e) => {
                              e.preventDefault();
                              setTopPreviewDragOver(i);
                            }}
                            onDrop={() => handleTopPreviewDrop(i)}
                            onDragLeave={() => setTopPreviewDragOver(null)}
                            onDragEnd={() => {
                              setTopPreviewDragIdx(null);
                              setTopPreviewDragOver(null);
                            }}
                            menuRef={setItemMenuRef(item.id)}
                            onMenu={() => setOpenItemMenu(item.id)}
                            onGripPointerDown={topReorder.startDrag(item.id)}
                            content={item.kind === "properties" ? propertiesPreviewContent : relationsPreviewContent}
                          />
                        ) : (
                          <div className={`w-full ${topPreviewDragIdx === i ? "opacity-40" : "opacity-100"} ${topPreviewDragOver === i && topPreviewDragIdx !== i ? layoutEditorRingDragOverClass : ""}`}>
                            <PanelPropertyCard
                              card={item.card}
                              valueContent={renderPanelCardValue(item.card)}
                              active={openItemMenu === item.id}
                              menuRef={setItemMenuRef(item.id)}
                              onMenu={() => setOpenItemMenu(item.id)}
                              onGripPointerDown={topReorder.startDrag(item.id)}
                            />
                          </div>
                        )}
                      </div>
                    );})}
                  </div>

                  {openItemMenu && (() => {
                    const key = openItemMenu;
                    const anchor = itemMenuBtnRefs.current.get(key) ?? null;
                    const close = () => { setOpenItemMenu(null); setGroupMenuSearch(""); setAdvancedOpen(false); };
                    const index = topPreviewOrder.indexOf(key);
                    const moveItems = [
                      ...(index > 0
                        ? [{ id: "up", label: "Move up", icon: <ArrowUp className="h-4 w-4" />, onClick: () => moveTopItem(key, -1) }]
                        : []),
                      ...(index >= 0 && index < topPreviewOrder.length - 1
                        ? [{ id: "down", label: "Move down", icon: <ArrowDown className="h-4 w-4" />, onClick: () => moveTopItem(key, 1) }]
                        : []),
                    ];
                    const popoverClass = "border-component w-[280px] rounded-lg border bg-modal p-1 text-popover-foreground shadow-md";
                    const heading = (text: string) => <p className="px-2 pb-1 pt-1 text-[13px] font-medium text-foreground">{text}</p>;
                    const propRow = (id: string, right: React.ReactNode) => (
                      <div key={id} className="flex items-center gap-2 rounded-md px-2 py-1.5 hover-component">
                        <span className="flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground">{propertyIconFor(id)}</span>
                        <span className="min-w-0 flex-1 truncate text-sm text-foreground">{boardProperties?.[id]?.name || "Property"}</span>
                        {right}
                      </div>
                    );

                    if (key === "discussions" || key === "content") {
                      const mi = moduleIds.indexOf(key);
                      return (
                        <AnchoredPopover anchor={anchor} placement="right-start" offsetPx={8} zIndex={100010} onClose={close} className={popoverClass}>
                          <DropdownMenu items={[
                            ...(mi > 0
                              ? [{ id: "up", label: "Move up", icon: <ArrowUp className="h-4 w-4" />, onClick: () => moveModule(key, -1) }]
                              : []),
                            ...(mi >= 0 && mi < moduleIds.length - 1
                              ? [{ id: "down", label: "Move down", icon: <ArrowDown className="h-4 w-4" />, onClick: () => moveModule(key, 1) }]
                              : []),
                            ...(key === "discussions" ? [{
                              id: "hide",
                              label: "Hide page discussions",
                              icon: <EyeOff className="h-4 w-4" />,
                              onClick: () => { set("pageDiscussions", false); close(); },
                            }] : []),
                          ]} />
                        </AnchoredPopover>
                      );
                    }

                    if (key.startsWith("staging:")) {
                      const card = panelStagingCards.find((c) => `staging:${c.id}` === key);
                      if (!card) return null;
                      return (
                        <AnchoredPopover anchor={anchor} placement="left-start" offsetPx={8} zIndex={100010} onClose={close} className={popoverClass}>
                          {heading(card.name)}
                          <DropdownMenu items={[
                            { id: "page", label: "Move to page", icon: <ArrowLeft className="h-4 w-4" />, onClick: () => { moveStagingCardToPage(card); close(); } },
                            { id: "group", label: "Add to property group", icon: <SquarePlus className="h-4 w-4" />, onClick: () => { removeStagingCard(card); close(); } },
                          ]} />
                        </AnchoredPopover>
                      );
                    }

                    if (key === "properties") {
                      const q = groupMenuSearch.trim().toLowerCase();
                      const match = (id: string) => !q || String(boardProperties?.[id]?.name ?? "").toLowerCase().includes(q);
                      const shown = resolvedPropertyOrder.filter((id) => !!boardProperties?.[id] && !panelPropertyIds.has(id) && match(id));
                      const hiddenIds = panelStagingCards.map((c) => c.propertyId).filter((id): id is string => !!id && !!boardProperties?.[id] && match(id));
                      return (
                        <AnchoredPopover anchor={anchor} placement="right-start" offsetPx={8} zIndex={100010} onClose={close} className={popoverClass}>
                          {heading("Property group")}
                          <div className="border-component mx-1 mb-2 flex h-8 items-center gap-2 rounded-md border px-2">
                            <Search className="h-4 w-4 text-muted-foreground" />
                            <input value={groupMenuSearch} onChange={(e) => setGroupMenuSearch(e.target.value)} placeholder="Search properties…" className="w-full bg-transparent text-sm outline-none" />
                          </div>
                          <div ref={groupReorder.scrollerRef} className="clean-scrollbar max-h-[min(320px,45vh)] overflow-y-auto">
                            {shown.map((id, index) => (
                              <div key={id} {...groupReorder.itemProps(id, index)}>
                                <div className="group flex items-center gap-2 rounded-md px-2 py-1.5 hover-component">
                                  <span className="relative flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground">
                                    <span className={q ? "" : "group-hover:opacity-0"}>{propertyIconFor(id)}</span>
                                    {!q && (
                                      <GripVertical
                                        aria-label="Drag to reorder"
                                        onPointerDown={groupReorder.startDrag(id)}
                                        className="absolute inset-0 h-4 w-4 cursor-grab opacity-0 group-hover:opacity-100 active:cursor-grabbing"
                                      />
                                    )}
                                  </span>
                                  <span className="min-w-0 flex-1 truncate text-sm text-foreground">{boardProperties?.[id]?.name || "Property"}</span>
                                  <button type="button" aria-label="Hide (move to panel)" onClick={() => hideGroupProperty(id)} className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground"><Eye className="h-4 w-4" /></button>
                                </div>
                              </div>
                            ))}
                            {hiddenIds.map((id) => propRow(id, (
                              <button type="button" aria-label="Show in group" onClick={() => showGroupProperty(id)} className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground/60 hover:text-foreground"><EyeOff className="h-4 w-4" /></button>
                            )))}
                            {shown.length + hiddenIds.length === 0 && <p className="px-2 py-1 text-[13px] text-muted-foreground/60">No properties</p>}
                          </div>
                          <div className="border-component mt-1 border-t pt-1">
                            <DropdownMenu items={[
                              ...moveItems,
                              { id: "panel", label: "Move to panel", icon: <ArrowRight className="h-4 w-4" />, disabled: shown.length === 0, onClick: () => { moveGroupToPanel(); close(); } },
                            ]} />
                          </div>
                        </AnchoredPopover>
                      );
                    }

                    if (key === "relations") {
                      return (
                        <AnchoredPopover anchor={anchor} placement="right-start" offsetPx={8} zIndex={100010} onClose={close} className={popoverClass}>
                          {heading("Relations group")}
                          <p className="px-2 pb-2 text-[13px] text-muted-foreground">Relation properties shown together as one section.</p>
                          <div ref={relationsReorder.scrollerRef}>
                            {(relationsGroupIds ?? []).map((id, index) => (
                              <div key={id} {...relationsReorder.itemProps(id, index)}>
                                <div className="group flex items-center gap-2 rounded-md px-2 py-1.5 hover-component">
                                  <span className="relative flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground">
                                    <span className="group-hover:opacity-0">{propertyIconFor(id)}</span>
                                    <GripVertical
                                      aria-label="Drag to reorder"
                                      onPointerDown={relationsReorder.startDrag(id)}
                                      className="absolute inset-0 h-4 w-4 cursor-grab opacity-0 group-hover:opacity-100 active:cursor-grabbing"
                                    />
                                  </span>
                                  <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                                    {boardProperties?.[id]?.name || "Property"}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="border-component mt-1 border-t pt-1">
                            <DropdownMenu items={[
                              ...moveItems,
                              { id: "group", label: "Add to property group", icon: <SquarePlus className="h-4 w-4" />, onClick: () => { dissolveRelationsGroup(); close(); } },
                              { id: "panel", label: "Move to panel", icon: <ArrowRight className="h-4 w-4" />, onClick: () => { moveRelationsGroupToPanel(); close(); } },
                            ]} />
                          </div>
                        </AnchoredPopover>
                      );
                    }

                    const card = panelCards.find((c) => c.id === key);
                    if (!card) return null;
                    const isRelation = !!card.propertyId && boardProperties?.[card.propertyId]?.type === "relation";
                    return (
                      <AnchoredPopover anchor={anchor} placement="right-start" offsetPx={8} zIndex={100010} onClose={close} className={popoverClass}>
                        {heading(card.name)}
                        <DropdownMenu items={[
                          ...moveItems,
                          { id: "panel", label: "Move to panel", icon: <ArrowRight className="h-4 w-4" />, onClick: () => { moveCardToPanel(card); close(); } },
                          { id: "group", label: "Add to property group", icon: <SquarePlus className="h-4 w-4" />, onClick: () => { removeCardFromPreview(card); close(); } },
                        ]} />
                        {isRelation && (
                          <div ref={advancedRowRef} onMouseEnter={() => setAdvancedOpen(true)}>
                            <DropdownMenuItem
                              id="advanced"
                              label="Advanced"
                              icon={<Settings className="h-4 w-4" />}
                              hasChevron
                              selected={advancedOpen}
                              onClick={() => setAdvancedOpen((v) => !v)}
                            />
                          </div>
                        )}
                        {isRelation && advancedOpen && (
                          <AnchoredPopover
                            anchor={advancedRowRef}
                            placement="right-start"
                            offsetPx={4}
                            zIndex={100011}
                            lockScroll={false}
                            onClose={() => setAdvancedOpen(false)}
                            className={popoverClass}
                          >
                            <DropdownMenu items={[{
                              id: "relations",
                              label: relationsGroupIds ? "Add to relations group" : "Convert to relations group",
                              subtitle: "Show relations together as one section",
                              icon: renderTypeIcon("relation"),
                              onClick: () => { convertCardToRelationsGroup(card); close(); },
                            }]} />
                          </AnchoredPopover>
                        )}
                      </AnchoredPopover>
                    );
                  })()}

                  {isPreviewDropActive && (
                    <div className="h-[4px] w-full bg-[rgba(35,131,226,0.45)] dark:bg-[rgba(35,131,226,0.55)] rounded-full my-1 shadow-[0_0_8px_rgba(35,131,226,0.2)] transition-all duration-200" />
                  )}

                  {/* Blue placeholder in preview when addToPreviewMode is active */}
                  {addToPreviewMode && (
                    <div className="layout-content mb-1">
                      <div className="h-[120px] w-full rounded-[12px] flex items-center justify-center shadow-[0_0_0_0.01684px_rgba(35,131,226,0.35),0_0_0.03368px_0.03368px_rgba(35,131,226,0.14)]">
                        <div className="h-[120px] w-full rounded-[12px] bg-[#eaf4fc] shadow-[inset_0_0_0_1px_rgba(35,131,226,0.38)] dark:bg-[#252a33] dark:shadow-[inset_0_0_0_1px_rgba(35,131,226,0.42)]" />
                      </div>
                    </div>
                  )}

                  <div className="flex justify-center py-1">
                    <button
                      type="button"
                      onClick={() => setAddToPreviewMode(true)}
                      className={`flex h-8 w-8 items-center justify-center rounded-full bg-[#dfeefb] transition-colors hover:opacity-90 dark:bg-[#2a3347] ${layoutEditorAccentIconClass}`}
                      aria-label="Add to page panel"
                    >
                      <Plus className="h-5 w-5" />
                    </button>
                  </div>

                  <div ref={moduleReorder.scrollerRef} className="flex w-full flex-col gap-3">
                  {modulesAfterPanel.map((mod) => {
                    const i = modules.findIndex((m) => m.id === mod.id);
                    return (
                    <div key={mod.id} {...moduleReorder.itemProps(mod.id, moduleIds.indexOf(mod.id))}>
                    <ModuleCard
                      mod={mod}
                      dragging={dragIdx === i}
                      dragOver={dragOver === i && dragIdx !== i}
                      active={openItemMenu === mod.id}
                      onDragStart={() => handleDragStart(i)}
                      onDragOver={(e) => handleDragOver(i, e)}
                      onDragLeave={() => setDragOver(null)}
                      onDrop={() => handleDrop(i)}
                      onDragEnd={() => { setDragIdx(null); setDragOver(null); }}
                      onRemove={() => {
                        setModules((ms) => ms.filter((_, idx) => idx !== i));
                        if (mod.id === "discussions") {
                          set("pageDiscussions", false);
                        }
                      }}
                      configureRef={setItemMenuRef(mod.id)}
                      onConfigure={() => setOpenItemMenu(mod.id)}
                      onGripPointerDown={moduleReorder.startDrag(mod.id)}
                      propertiesPreviewContent={propertiesPreviewContent}
                      pageTitle={previewTitleRaw}
                      pageIcon={previewIcon}
                    />
                    </div>
                  );})}
                  </div>

                </div>
              </div>

               <div className="w-[428px] min-w-[428px] max-w-[428px] overflow-y-auto clean-scrollbar border-l border-[rgba(15,15,15,0.09)] bg-white px-6 pb-24 pt-7 dark:border-[rgba(255,255,255,0.09)] dark:bg-[#202020]">
                {!addToPanelMode && panelStagingCards.length === 0 && (
                  <div className="flex justify-center mb-3">
                    <button
                      ref={addToPanelTriggerRef}
                      type="button"
                      onClick={() => {
                        setAddToPanelMode(true);
                      }}
                      className="inline-flex items-center gap-2 rounded-md bg-[#eaf4fc] px-3 py-2 text-sm font-medium text-[#206694] transition-colors hover:bg-[#dfeefb] dark:bg-[#252a33] dark:text-[#9ecbff] dark:hover:bg-[#2a3347]"
                    >
                      <Plus className="h-4 w-4" />
                      Add to panel
                    </button>
                  </div>
                )}
                <div className="space-y-3 pb-24">
                  {addToPanelMode && (
                    <div className="layout-content pb-3">
                      <div className="h-[160px] w-full rounded-[12px] flex items-center justify-center shadow-[0_0_0_0.01684px_rgba(35,131,226,0.35),0_0_0.03368px_0.03368px_rgba(35,131,226,0.14)]">
                        <div className="h-[160px] w-full rounded-[12px] bg-[#eaf4fc] shadow-[inset_0_0_0_1px_rgba(35,131,226,0.38)] dark:bg-[#252a33] dark:shadow-[inset_0_0_0_1px_rgba(35,131,226,0.42)]" />
                      </div>
                    </div>
                  )}
                  {panelStagingCards.map((card) => (
                    <div
                      key={card.id}
                      draggable
                      onDragStart={() => setDraggingStagingCardId(card.id)}
                      onDragEnd={() => {
                        setDraggingStagingCardId(null);
                        setIsPreviewDropActive(false);
                      }}
                      className="flex flex-col cursor-grab"
                    >
                      <PanelPropertyCard
                        card={card}
                        valueContent={renderPanelCardValue(card)}
                        active={openItemMenu === `staging:${card.id}`}
                        menuRef={setItemMenuRef(`staging:${card.id}`)}
                        onMenu={() => setOpenItemMenu(`staging:${card.id}`)}
                      />
                    </div>
                  ))}
                  {panelStagingCards.length > 0 && !addToPanelMode && (
                    <div className="flex justify-center pt-1">
                      <button
                        type="button"
                        onClick={() => setAddToPanelMode(true)}
                        className={`flex h-8 w-8 items-center justify-center rounded-full bg-[#dfeefb] transition-colors hover:opacity-90 dark:bg-[#2a3347] ${layoutEditorAccentIconClass}`}
                        aria-label="Add a property"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Settings panel */}
        <aside ref={settingsPanelRef} className="w-[290px] shrink-0 border-l border-border/60 overflow-y-auto clean-scrollbar px-4 py-5">
          {(addToPanelMode || addToPreviewMode) ? (
            <div className="space-y-3">
              <p className="text-[16px] leading-6 font-medium text-foreground px-2">
                {addToPreviewMode ? "Add to preview" : "Add to panel"}
              </p>
              <div className="px-1">
                <div className="h-8 rounded-md border border-border flex items-center gap-2 px-2">
                  <Search className="h-4 w-4" />
                  <input
                    value={panelSearch}
                    onChange={(e) => setPanelSearch(e.target.value)}
                    placeholder="Search..."
                    className="w-full bg-transparent text-sm outline-none"
                  />
                </div>
              </div>

              {filteredExistingProperties.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[11px] font-semibold text-muted-foreground px-2">Use an existing property</p>
                  <div className="space-y-1">
                    {filteredExistingProperties.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => addToPreviewMode
                          ? addPreviewCard(p.name, p.type, p.icon, p.id)
                          : addPanelCard(p.name, p.type, p.icon, p.id)
                        }
                        className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 hover-component transition-colors text-left"
                      >
                        <div className="h-8 w-8 rounded border border-border bg-background flex items-center justify-center text-muted-foreground">
                          {p.icon}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm text-foreground truncate">{p.name}</div>
                          <div className="text-xs text-muted-foreground">{getPropertyLabel(p.type)}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <p className="text-[11px] font-semibold text-muted-foreground px-2">Create new property</p>
                <div className="space-y-1">
                  {filteredNewTemplates.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => addToPreviewMode
                        ? addPreviewCard(t.name, t.name, t.icon, undefined, t.type)
                        : addPanelCard(t.name, t.name, t.icon, undefined, t.type)
                      }
                      className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 hover-component transition-colors text-left"
                    >
                      <div className="h-8 w-8 rounded border border-border bg-background flex items-center justify-center text-muted-foreground">
                        {t.icon}
                      </div>
                      <div className="text-sm text-foreground truncate min-w-0">{t.name}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <>
              <p className="text-[15px] font-semibold text-foreground mb-5">Page settings</p>

              {/* Structure */}
              <p className="text-[11px] font-semibold text-muted-foreground mb-3">Structure</p>
              <div className="flex gap-3 mb-6">
                {(["simple", "tabbed"] as Structure[]).map(s => (
                  <button key={s} type="button" id={`layout-structure-${s}`}
                    onClick={() => set("structure", s)}
                    className="flex flex-col items-center flex-1 gap-1.5 focus:outline-none">
                    <div className={`w-full h-[64px] rounded-lg border-2 flex items-center justify-center transition-all
                  ${settings.structure === s
                    ? "border-blue-500 shadow-[0_0_0_1px_rgba(59,130,246,0.5)]"
                    : "border-border hover:border-muted-foreground/40"} bg-muted/30`}>
                      {s === "simple"
                        ? <div className="flex flex-col gap-1 w-9">
                            <div className="h-2 bg-foreground/25 rounded" />
                            <div className="h-1 bg-foreground/12 rounded w-3/4" />
                            <div className="h-1 bg-foreground/12 rounded w-1/2" />
                          </div>
                        : <div className="flex flex-col gap-1 w-9">
                            <div className="flex gap-1">
                              <div className="h-1.5 bg-blue-400/70 rounded flex-1" />
                              <div className="h-1.5 bg-foreground/12 rounded flex-1" />
                            </div>
                            <div className="h-1 bg-foreground/12 rounded" />
                            <div className="h-1 bg-foreground/12 rounded w-3/4" />
                          </div>}
                    </div>
                    <span className={`text-[12px] ${settings.structure === s ? "font-semibold text-foreground" : "text-muted-foreground"}`}>
                      {s === "simple" ? "Simple" : "Tabbed"}
                    </span>
                  </button>
                ))}
              </div>

              {/* Options */}
              <p className="text-[11px] font-semibold text-muted-foreground mb-2">Options</p>
              <div className="flex flex-col divide-y divide-border/40">

                {/* Inline comments dropdown */}
                <div className="flex items-center justify-between py-2.5 gap-2">
                  <span className="text-[13px] text-foreground">Inline comments</span>
                  <div className="relative">
                    <button type="button" id="layout-inline-comments"
                      onClick={() => setInlineOpen(p => !p)}
                      className="flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground transition-colors">
                      {inlineLabels[settings.inlineComments]}<ChevronDown className="h-3 w-3" />
                    </button>
                    {inlineOpen && (
                      <SelfAnchoredPopover
                        placement="bottom-end"
                        onClose={() => setInlineOpen(false)}
                        className="w-36 bg-popover border border-border rounded-lg shadow-lg py-1"
                      >
                        {(["default", "minimal", "hidden"] as const).map(v => (
                          <button key={v} type="button"
                            onClick={() => { set("inlineComments", v); setInlineOpen(false); }}
                            className={`w-full text-left px-3 py-1.5 text-[13px] hover-component transition-colors flex items-center justify-between ${settings.inlineComments === v ? "text-blue-500" : "text-foreground"}`}>
                            {inlineLabels[v]}
                            {settings.inlineComments === v && <Check className="h-3 w-3" />}
                          </button>
                        ))}
                      </SelfAnchoredPopover>
                    )}
                  </div>
                </div>
                {/* Toggles */}
                {([
                  { key: "pageDiscussions", label: "Page discussions" },
                  { key: "propertyIcons", label: "Property icons" },
                  { key: "fullWidth", label: "Full width", info: true },
                  { key: "dbTemplates", label: "Database templates", info: true },
                ] as { key: keyof PageLayoutEditorState; label: string; info?: boolean }[]).map(({ key, label, info }) => (
                  <div key={key} className="flex items-center justify-between py-2.5 gap-2">
                    <div className="flex items-center gap-1">
                      <label htmlFor={`layout-${key}`} className="text-[13px] text-foreground cursor-pointer">{label}</label>
                      {info && <Info className="h-3 w-3 text-muted-foreground" />}
                    </div>
                    <Toggle id={`layout-${key}`} checked={!!settings[key]} onChange={v => set(key, v as any)} />
                  </div>
                ))}
              </div>
            </>
          )}
        </aside>
      </div>

      {showResetLayoutModal && (
        <div
          className="fixed inset-0 z-[100100] flex items-center justify-center"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowResetLayoutModal(false)}
          />
          <div className="relative z-[1] w-[300px] rounded-xl border border-border bg-popover p-5 shadow-2xl">
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => setShowResetLayoutModal(false)}
              className="absolute right-2 top-2 h-6 w-6 rounded-full text-muted-foreground hover-component"
            >
              <X className="mx-auto h-4 w-4" />
            </button>
            <div className="mb-5 mt-2 text-center">
              <div className="mb-2 text-[17px] font-semibold leading-6 text-foreground">
                Reset to original page layout?
              </div>
              <div className="text-[14px] text-muted-foreground">
                No properties will be deleted.
              </div>
            </div>
            <div className="flex justify-center gap-2">
              <button
                type="button"
                onClick={() => void handleResetToOriginalLayout()}
                className="h-8 rounded-md bg-red-500 px-3 text-sm font-medium text-white hover:bg-red-600"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={() => setShowResetLayoutModal(false)}
                className="h-8 rounded-md px-3 text-sm font-medium text-muted-foreground hover-component"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}
